import { useEffect, useState, useRef } from 'react';

/**
 * Hook para aplicar debounce a un valor
 *
 * Útil para búsquedas en tiempo real, validaciones, y cualquier operación
 * que deba esperar a que el usuario termine de escribir/interactuar.
 *
 * @param value - Valor a aplicar debounce
 * @param delay - Tiempo de espera en milisegundos (default: 500ms)
 * @returns Valor debounced que se actualiza después del delay
 *
 * @example
 * ```typescript
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 500);
 *
 * useEffect(() => {
 *   if (debouncedSearchTerm.length >= 3) {
 *     fetchResults(debouncedSearchTerm);
 *   }
 * }, [debouncedSearchTerm]);
 * ```
 */
function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Limpiar timer anterior si existe
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Crear nuevo timer
    timerRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup: limpiar timer en unmount o cuando value/delay cambian
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

export default useDebounce;
